import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTopBar } from '@/contexts/TopBarContext'
import { adminApi, type CourseDto, type CourseSave, type CertificateDesignDto, type CertificateFieldConfig } from '@/api/admin'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Plus, Pencil, Trash2, X, Award, Tags, Code2 } from 'lucide-react'
import { usePagination } from '@/hooks/usePagination'
import { TablePagination } from '@/components/shared/TablePagination'
import ProUpgradeDialog from '@/components/shared/ProUpgradeDialog'
import { useAdminMoney } from '@/hooks/useMoney'
import { formatDate } from '@/lib/dates'
import { formatCourseDuration } from '@/lib/duration'
import CourseDurationFields from './CourseDurationFields'
import CourseCategorySelect from './CourseCategorySelect'
import ManageCategoriesDialog from './ManageCategoriesDialog'
import CourseEmbedModal from './CourseEmbedModal'

// ─── Types ──────────────────────────────────────────────────────────────────
type CourseFormData = CourseSave

const empty: CourseFormData = {
  name: '', code: null, category: null, categoryId: null, description: null, websiteUrl: null,
  price: null, durationMinutes: null, defaultDays: 1, durationLabel: null, defaultExpiryMonths: null,
  certificateDesignId: null,
  isThirdPartyCertified: false, thirdPartyProviderName: null, sendThirdPartyCompletionEmail: true,
  pricingModel: 'PerPerson', flatFeeAmount: null, flatFeeCapacity: null, requirePhotoCheck: false,
}

function coursePriceLabel(course: CourseDto, fmt: (n: number) => string): string | null {
  if (course.pricingModel === 'FlatFee') {
    if (course.flatFeeAmount == null) return null
    return course.flatFeeCapacity != null
      ? `${fmt(course.flatFeeAmount)} flat (up to ${course.flatFeeCapacity})`
      : `${fmt(course.flatFeeAmount)} flat`
  }
  return course.price != null ? `${fmt(course.price)}pp` : null
}

// ─── Certificate design preview ──────────────────────────────────────────────
function parseFields(json: string | null): CertificateFieldConfig[] {
  if (!json) return []
  try { return JSON.parse(json) } catch { return [] }
}

function previewText(cfg: CertificateFieldConfig, companyName: string | null, region?: string | null): string {
  const today = new Date()
  const nextYear = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())
  const fmt = (d: Date) => formatDate(d, region, { day: 'numeric', month: 'long', year: 'numeric' })
  switch (cfg.field) {
    case 'recipientName':  return 'Recipient Name'
    case 'courseName':     return 'Course Name'
    case 'courseCode':     return 'CODE-01'
    case 'completedDate':  return fmt(today)
    case 'expiryDate':     return fmt(nextYear)
    case 'companyName':    return companyName || 'Your Company'
    case 'static':         return cfg.staticText || 'Custom text'
    case 'companyLogo':        return ''
    case 'image':              return ''
    case 'verificationQr':     return ''
    case 'trainerSignature':   return ''
    case 'companySignature':   return ''
    default:                   return ''
  }
}

function CertificateDesignPreview({ design }: { design: CertificateDesignDto }) {
  const canvasRef = useRef<HTMLDivElement>(null)
  const [canvasH, setCanvasH] = useState(0)

  useEffect(() => {
    const el = canvasRef.current
    if (!el) return
    const obs = new ResizeObserver(() => setCanvasH(el.offsetHeight))
    obs.observe(el)
    setCanvasH(el.offsetHeight)
    return () => obs.disconnect()
  }, [])

  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })

  const fields = parseFields(design.fieldsJson)
  const companyName = profile?.displayName ?? null
  const region = profile?.region

  return (
    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-white" style={{ width: 280 }}>
      <div ref={canvasRef}
        className="relative overflow-hidden bg-gray-100 select-none"
        style={{ aspectRatio: design.isPortrait ? '1 / 1.414' : '1.414 / 1' }}
      >
        {(() => {
          const bgUrl = design.templateBackgroundImageUrl ?? design.systemTemplateThumbnailUrl ?? null
          return bgUrl
            ? <>
                <img src={bgUrl} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
                {design.systemCertificateTemplateId && design.primaryColour && (
                  <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: design.primaryColour, mixBlendMode: 'screen' }} />
                )}
              </>
            : <div className="absolute inset-0 flex items-center justify-center text-gray-300 text-xs">No background</div>
        })()}

        {fields.map((el, i) => {
          const isLogo = el.field === 'companyLogo'
          return (
            <div key={i}
              className="absolute pointer-events-none"
              style={{
                left: `${el.x}%`, top: `${el.y}%`,
                transform: 'translate(-50%, -50%)',
                ...((isLogo || el.field === 'image')
                  ? { width: `${isLogo ? (el.logoWidth ?? 20) : (el.imageWidth ?? 20)}%` }
                  : {
                    fontSize: canvasH ? `${el.fontSize * (canvasH / 1000)}px` : undefined,
                    fontWeight: el.bold ? 700 : 400,
                    color: el.color,
                    textAlign: el.align as React.CSSProperties['textAlign'],
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    textShadow: '0 1px 2px rgba(255,255,255,0.5)',
                  }),
              }}
            >
              {isLogo
                ? profile?.logoUrl
                  ? <img src={profile.logoUrl} alt="" className="w-full h-auto" draggable={false} />
                  : <div className="border border-dashed border-gray-400/60 rounded px-1 py-0.5 text-gray-400 text-[9px] whitespace-nowrap">Logo</div>
                : el.field === 'image'
                  ? el.imageUrl
                    ? <img src={el.imageUrl} alt="" className="w-full h-auto" draggable={false} />
                    : null
                  : previewText(el, companyName, region)}
            </div>
          )
        })}

        {fields.length === 0 && (design.templateBackgroundImageUrl ?? design.systemTemplateThumbnailUrl) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-xs text-white/70 bg-black/20 px-2 py-1 rounded-full">No fields configured</span>
          </div>
        )}
      </div>
      <div className="px-2.5 py-1.5 flex items-center justify-between border-t border-gray-100 bg-gray-50">
        <span className="text-xs font-medium text-gray-700 truncate">{design.name}</span>
        <span className="text-[10px] text-gray-400 ml-2 shrink-0">{design.templateName}</span>
      </div>
    </div>
  )
}

// ─── Form field wrapper ──────────────────────────────────────────────────────
function Field({ id, label, children }: { id?: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  )
}

// ─── Course form ─────────────────────────────────────────────────────────────
function CourseForm({
  initial, onSubmit, onCancel, pending, title,
}: {
  initial: CourseFormData
  onSubmit: (data: CourseFormData) => void
  onCancel: () => void
  pending: boolean
  title: string
}) {
  const { symbol } = useAdminMoney()
  const [form, setForm] = useState<CourseFormData>(initial)
  const [showProDialog, setShowProDialog] = useState(false)

  const { data: designs = [] } = useQuery({
    queryKey: ['certificate-designs'],
    queryFn: () => adminApi.getCertificateDesigns().then(r => r.data),
  })
  const { data: companyProfile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })
  const isPro = companyProfile?.planTier === 'Pro'

  const set = (key: keyof CourseFormData, value: string) =>
    setForm(f => ({ ...f, [key]: value === '' ? null : value }))
  const setNum = (key: keyof CourseFormData, value: string) =>
    setForm(f => ({ ...f, [key]: value === '' ? null : parseFloat(value) }))
  const setInt = (key: keyof CourseFormData, value: string) =>
    setForm(f => ({ ...f, [key]: value === '' ? null : parseInt(value) }))

  return (
    <Card>
      <CardHeader className="pb-4 flex flex-row items-center justify-between">
        <CardTitle className="text-base">{title}</CardTitle>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400" onClick={onCancel}>
          <X size={15} />
        </Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={e => { e.preventDefault(); onSubmit(form) }} className="space-y-5">
          {/* Row 1: Name + Code */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="col-span-2">
              <Field id="name" label="Course name *">
                <Input id="name" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Manual Handling" />
              </Field>
            </div>
            <Field id="code" label="Course code">
              <Input id="code" value={form.code ?? ''} onChange={e => set('code', e.target.value)} placeholder="e.g. MH-01" />
            </Field>
          </div>

          {/* Row 2: Category + Expiry */}
          <div className="grid grid-cols-2 gap-4">
            <Field id="category" label="Category">
              <CourseCategorySelect
                id="category"
                categoryId={form.categoryId}
                legacyName={form.category}
                onChange={(categoryId, category) => setForm(f => ({ ...f, categoryId, category }))}
              />
            </Field>
            <Field id="defaultExpiryMonths" label="Renews every (months)">
              <Input id="defaultExpiryMonths" type="number" min="1" step="1" value={form.defaultExpiryMonths ?? ''} onChange={e => setInt('defaultExpiryMonths', e.target.value)} placeholder="e.g. 12" />
            </Field>
          </div>

          {/* Duration */}
          <CourseDurationFields value={form} onChange={patch => setForm(f => ({ ...f, ...patch }))} />

          {/* Pricing */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Pricing</Label>
            <div className="flex gap-1 mt-1.5 mb-3 rounded-lg bg-gray-100 p-1 w-fit">
              {(['PerPerson', 'FlatFee'] as const).map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, pricingModel: m }))}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors cursor-pointer ${
                    form.pricingModel === m ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {m === 'PerPerson' ? 'Per person' : 'Flat fee'}
                </button>
              ))}
            </div>
            {form.pricingModel === 'PerPerson' ? (
              <Field id="price" label={`Price per person (${symbol})`}>
                <Input id="price" type="number" min="0" step="0.01" value={form.price ?? ''} onChange={e => setNum('price', e.target.value)} placeholder="0.00" className="max-w-xs" />
              </Field>
            ) : (
              <div className="grid grid-cols-2 gap-4 max-w-md">
                <Field id="flatFeeAmount" label={`Flat fee (${symbol}) *`}>
                  <Input id="flatFeeAmount" type="number" min="0" step="0.01" value={form.flatFeeAmount ?? ''} onChange={e => setNum('flatFeeAmount', e.target.value)} placeholder="e.g. 500.00" required />
                </Field>
                <Field id="flatFeeCapacity" label="Up to how many people?">
                  <Input id="flatFeeCapacity" type="number" min="1" step="1" value={form.flatFeeCapacity ?? ''} onChange={e => setInt('flatFeeCapacity', e.target.value)} placeholder="e.g. 12" />
                </Field>
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1.5">
              {form.pricingModel === 'FlatFee'
                ? 'Charged once per booking regardless of how many people attend. The capacity is shown to describe the package but isn’t enforced as a hard limit.'
                : 'Multiplied by the number of places booked.'}
            </p>
          </div>

          {/* Row 3: Description */}
          <Field id="description" label="Description">
            <textarea
              id="description"
              value={form.description ?? ''}
              onChange={e => set('description', e.target.value)}
              placeholder="Brief description of the course content…"
              rows={3}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
            />
          </Field>

          {/* Third-party certification */}
          <div className="border-t border-gray-100 pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                checked={form.isThirdPartyCertified}
                onChange={e => {
                  if (e.target.checked && !isPro) { setShowProDialog(true); return }
                  setForm(f => ({ ...f, isThirdPartyCertified: e.target.checked }))
                }}
              />
              <span>
                <span className="text-sm font-medium text-gray-900 inline-flex items-center gap-1.5">
                  Certified by a third party
                  {!isPro && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Pro</span>}
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  The certificate is issued by an external awarding body, not generated by CertSlate.
                  You'll upload and assign each certificate yourself once it arrives.
                </span>
              </span>
            </label>

            {form.isThirdPartyCertified && (
              <div className="mt-4 pl-7 space-y-4">
                <Field id="thirdPartyProviderName" label="Provider name *">
                  <Input
                    id="thirdPartyProviderName"
                    value={form.thirdPartyProviderName ?? ''}
                    onChange={e => set('thirdPartyProviderName', e.target.value)}
                    placeholder="e.g. NEBOSH"
                    required
                    className="max-w-xs"
                  />
                </Field>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    checked={form.sendThirdPartyCompletionEmail}
                    onChange={e => setForm(f => ({ ...f, sendThirdPartyCompletionEmail: e.target.checked }))}
                  />
                  <span className="text-sm text-gray-700">
                    Email the learner when they complete this course
                    <span className="block text-xs text-gray-500 mt-0.5">
                      A "thanks for completing" email, since there's no certificate to attach yet.
                    </span>
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Certificate design */}
          {!form.isThirdPartyCertified && (
            <div className="border-t border-gray-100 pt-4">
              <Field id="certificateDesignId" label="Certificate design">
                <select
                  id="certificateDesignId"
                  value={form.certificateDesignId ?? ''}
                  onChange={e => setForm(f => ({ ...f, certificateDesignId: e.target.value || null }))}
                  className="flex h-9 w-full max-w-xs rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="">— No certificate —</option>
                  {designs.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
                {designs.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No designs yet — create one in the Certificates section first.
                  </p>
                )}
              </Field>
              {(() => {
                const selected = designs.find(d => d.id === form.certificateDesignId)
                return selected ? (
                  <div className="mt-3">
                    <CertificateDesignPreview design={selected} />
                  </div>
                ) : null
              })()}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={pending}>{pending ? 'Saving…' : 'Save course'}</Button>
          </div>
        </form>
      </CardContent>
      <ProUpgradeDialog
        open={showProDialog}
        onClose={() => setShowProDialog(false)}
        featureName="Third-party certification"
      />
    </Card>
  )
}

// ─── Courses page ─────────────────────────────────────────────────────────────
export default function CoursesPage() {
  const { fmt: fmtMoney } = useAdminMoney()
  useTopBar(<span className="text-sm font-medium text-gray-900">Courses</span>)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [showAdd, setShowAdd] = useState(false)
  const [showCategories, setShowCategories] = useState(false)
  const [showEmbed, setShowEmbed] = useState(false)
  const [showUpgrade, setShowUpgrade] = useState(false)

  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const isPro = profile?.planTier === 'Pro'

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (data: CourseFormData) => adminApi.createCourse(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['courses'] })
      setShowAdd(false)
      navigate(`/admin/courses/${res.data.id}`)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: adminApi.deleteCourse,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['courses'] }),
    onError: () => alert('Cannot delete a course that has training records associated with it.'),
  })

  const sorted = [...courses].sort((a, b) => {
    const cA = a.code ?? '', cB = b.code ?? ''
    return cA !== cB ? cA.localeCompare(cB) : a.name.localeCompare(b.name)
  })
  const { paged, page, setPage, totalPages, total, pageSize, start } = usePagination(sorted)

  return (
    <div>
      <div className="flex items-center justify-end gap-2 mb-6">
        <Button size="sm" variant="outline" className="gap-2" onClick={() => setShowCategories(true)}>
          <Tags size={16} /> Categories
        </Button>
        <Button size="sm" variant="outline" className="gap-2" onClick={() => (isPro ? setShowEmbed(true) : setShowUpgrade(true))}>
          <Code2 size={16} /> Embed
          {!isPro && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">Pro</span>}
        </Button>
        <Button size="sm" className="gap-2" onClick={() => setShowAdd(v => !v)}>
          <Plus size={16} /> Add course
        </Button>
      </div>
      <ManageCategoriesDialog open={showCategories} onClose={() => setShowCategories(false)} />
      {showEmbed && <CourseEmbedModal onClose={() => setShowEmbed(false)} />}
      <ProUpgradeDialog open={showUpgrade} onClose={() => setShowUpgrade(false)} featureName="Course pages and embeds for your website" />

      {showAdd && (
        <div className="mb-6">
          <CourseForm
            title="New course"
            initial={empty}
            onSubmit={data => createMutation.mutate(data)}
            onCancel={() => setShowAdd(false)}
            pending={createMutation.isPending}
          />
        </div>
      )}

      {/* Mobile card list */}
      <div className="sm:hidden space-y-2">
        {isLoading && <p className="text-center text-gray-500 py-8">Loading…</p>}
        {!isLoading && courses.length === 0 && (
          <p className="text-center text-gray-400 py-8">No courses yet.</p>
        )}
        {paged.map(course => (
          <div
            key={course.id}
            onClick={() => navigate(`/admin/courses/${course.id}`)}
            className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-indigo-200"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {course.code && <span className="text-xs font-mono text-gray-400">{course.code}</span>}
                  <p className="font-medium text-gray-900">{course.name}</p>
                </div>
                {course.category && <p className="text-xs text-gray-500 mt-0.5">{course.category}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
                {course.certificateDesignName && <span title="Certificate configured"><Award size={15} className="text-indigo-500" /></span>}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                  onClick={() => { if (confirm(`Delete "${course.name}"?`)) deleteMutation.mutate(course.id) }}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              {formatCourseDuration(course, { compact: true }) && <span>{formatCourseDuration(course, { compact: true })}</span>}
              {coursePriceLabel(course, fmtMoney) && <span>{coursePriceLabel(course, fmtMoney)}</span>}
              {course.defaultExpiryMonths ? <span>Renews {course.defaultExpiryMonths}mo</span> : null}
            </div>
          </div>
        ))}
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block">
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Duration</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Renewal</TableHead>
                <TableHead className="text-center">Cert</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-8">Loading…</TableCell></TableRow>
              )}
              {!isLoading && courses.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-8">No courses yet.</TableCell></TableRow>
              )}
              {paged.map(course => (
                <TableRow key={course.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/admin/courses/${course.id}`)}>
                  <TableCell className="text-gray-500 font-mono text-xs">{course.code ?? '—'}</TableCell>
                  <TableCell>
                    <p className="font-medium">{course.name}</p>
                    {course.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{course.description}</p>}
                  </TableCell>
                  <TableCell className="text-gray-600 text-sm">{course.category ?? '—'}</TableCell>
                  <TableCell className="text-gray-600 text-sm text-right">{formatCourseDuration(course, { compact: true }) ?? '—'}</TableCell>
                  <TableCell className="text-gray-600 text-sm text-right">{coursePriceLabel(course, fmtMoney) ?? '—'}</TableCell>
                  <TableCell className="text-gray-600 text-sm text-right">{course.defaultExpiryMonths ? `${course.defaultExpiryMonths}mo` : '—'}</TableCell>
                  <TableCell className="text-center">
                    {course.isThirdPartyCertified
                      ? <span title={`Certified by ${course.thirdPartyProviderName || 'a third party'}`} className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">3rd party</span>
                      : course.certificateDesignName
                        ? <span title="Certificate configured"><Award size={15} className="mx-auto text-indigo-500" /></span>
                        : <span className="text-gray-300 text-xs">—</span>}
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
                        onClick={() => navigate(`/admin/courses/${course.id}`)} title="Edit course">
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                        onClick={() => { if (confirm(`Delete "${course.name}"?`)) deleteMutation.mutate(course.id) }}
                        disabled={deleteMutation.isPending}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
        </Card>
      </div>
    </div>
  )
}
